import { PushAnalyticsEventsService, StaticTooltipShowEvent } from '@alliance/ats/analytics/utils'
import { AtsStaticTooltipParentRefEnum, NgZoneHelperService } from '@alliance/shared/utils'
import { AfterViewInit, Directive, Inject, Input, OnDestroy, TemplateRef } from '@angular/core'
import { RxStateComponent } from '@alliance/shared/models'
import { StaticTooltipViewHelperService } from './services/static-tooltip-view-helper.service'
import { combineLatest, filter, fromEvent, map, switchMap, take, takeUntil } from 'rxjs'
import { TooltipPosition } from '@alliance/santa/tooltip'
import { DOCUMENT } from '@angular/common'

type State = {
  activeTooltipParentRef: AtsStaticTooltipParentRefEnum | null
  parents: AtsStaticTooltipParentRefEnum[] | null
  showTooltip: boolean
  isInView: boolean
  isClickedOutside: boolean
  renderTooltip: boolean
  offset: number
  tooltipSelector: string
}

@Directive()
export class BaseStaticTooltipDirective extends RxStateComponent<State> implements AfterViewInit, OnDestroy {
  @Input() public tooltipTemplate: TemplateRef<unknown> | null = null
  @Input() public tooltipPosition: TooltipPosition = TooltipPosition.bottom
  @Input() public tooltipTopOffsetPosition = 0
  @Input() public applyChangeOnClick = false
  @Input() public analyticsEventLabel: string | null = null

  public readonly state = this.connectViewState(['isInView', 'isClickedOutside', 'renderTooltip', 'showTooltip', 'offset', 'parents', 'activeTooltipParentRef'])
  public constructor(
    protected staticTooltipViewHelperService: StaticTooltipViewHelperService,
    @Inject(DOCUMENT) private readonly document: Document,
    private ngZoneHelper: NgZoneHelperService,
    private pushAnalyticsEventsService: PushAnalyticsEventsService
  ) {
    super()
    this.initState({
      activeTooltipParentRef: this.staticTooltipViewHelperService
        .select('activeTooltipParentRef')
        .pipe(switchMap(tooltipParent => this.select('parents').pipe(map(parents => parents?.find(parent => parent === tooltipParent) || null)))),
      parents: null,
      renderTooltip: false,
      isInView: false,
      offset: 0,
      isClickedOutside: false,
      showTooltip: combineLatest([this.select('activeTooltipParentRef'), this.select('isInView'), this.select('renderTooltip'), this.select('isClickedOutside')]).pipe(
        map(([activeTooltipParentRef, isInView, renderTooltip, isClickedOutside]) => !!activeTooltipParentRef && !!isInView && !!renderTooltip && !isClickedOutside)
      ),
      tooltipSelector: ''
    })

    this.hold(
      this.select('tooltipSelector').pipe(
        filter(Boolean),
        switchMap(() => fromEvent(this.document, 'mouseup').pipe(takeUntil(this.select('isClickedOutside').pipe(filter(Boolean), take(1))), this.ngZoneHelper.outsideNgZone()))
      ),
      event => {
        if (event.target instanceof HTMLElement && !event.target.classList.contains(this.get().tooltipSelector)) {
          this.set({ isClickedOutside: true })
        }
      }
    )
  }

  @Input() public set parents(parents: AtsStaticTooltipParentRefEnum[] | null) {
    this.set({ parents })
  }

  @Input() public set renderTooltip(renderTooltip: boolean) {
    this.set({ renderTooltip })
  }

  @Input() public set offset(offset: number) {
    this.set({ offset })
  }

  public ngAfterViewInit(): void {
    if (this.analyticsEventLabel) {
      this.hold(this.select('activeTooltipParentRef').pipe(filter(activeRef => !!activeRef)), () => {
        this.pushAnalyticsEventsService.pushEvent({
          event: StaticTooltipShowEvent,
          labelParam: this.analyticsEventLabel
        })
      })
    }
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy()
    this.staticTooltipViewHelperService.clearActiveParentState()
  }

  public setInViewState(event: IntersectionObserverEntry[]): void {
    const isInView = event.some(item => item.isIntersecting)
    this.set({ isInView })
  }

  public onClicked(): void {
    const activeTooltipParentRef = this.get().activeTooltipParentRef
    if (activeTooltipParentRef && this.applyChangeOnClick) {
      this.actionApplied(activeTooltipParentRef)
    }
  }

  public setClosed(): void {
    this.set({ isClickedOutside: true })
  }

  private actionApplied(key: AtsStaticTooltipParentRefEnum): void {
    const currentActiveParent = this.staticTooltipViewHelperService.get().activeTooltipParentRef
    if (currentActiveParent === key) {
      this.staticTooltipViewHelperService.clearActiveParentState()
      this.staticTooltipViewHelperService.preventShowingTooltipByParentKey(key)
    }
  }
}
